/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import IReelManager from '../Reel/IReelManager';
import ReelManager from '../Reel/ReelManager';
import IPlayResultComponent from '../Server/Object/IPlayResultComponent';
import PlayResultComponent from '../Server/PlayResultComponent';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import ISymbolData from './ISymbolData';
import StreakTrackerRuleBase from './Rules/StreakTrackerRuleBase';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
export default class StreakTrackerComponent extends StateDependencyComponent
{
    /** Reference to [[StateComponent]] on which to run rules. */
    @property({ type: [StateReference], visible: true })
    private _streakOnStates: StateReference[] = [new StateReference(StateID.Spinning, false)];

    /** Reference to [[StateComponent]] on which to clear streaks. */
    @property({ type: [StateReference], visible: true })
    private _clearOnStates: StateReference[] = [new StateReference(StateID.Request, false)];

    /** If true, will execute on [[StateComponent]] instead of [[ReelEvent]]. */
    @property()
    protected executeOnState: boolean = false;

    /** If true, will execute continuously during state. */
    @property()
    protected executeContinuously: boolean = false;

    /** [[ReelManager]] reference to send streak events. */
    @property({ type: InjectableComponent, visible: true })
    private _reelManagerRef: InjectableComponent<IReelManager> = new InjectableComponent<IReelManager>();
    /** Public setter for [[ReelManager]] ref. */
    public set ReelManager(value: IReelManager) { this._reelManagerRef.Value = value; }
    public get ReelManager(): IReelManager { return this._reelManagerRef.Value; }

    /** [[IPlayResultComponent]] reference for result payline information. */
    @property({ type: InjectableComponent, visible: true })
    private _playResultRef: InjectableComponent<IPlayResultComponent> = new InjectableComponent<IPlayResultComponent>();
    /** Public setter for [[IPlayResultComponent]] ref. */
    public set PlayResult(value: IPlayResultComponent) { this._playResultRef.Value = value; }

    /** Child rules. */
    private _rules: StreakTrackerRuleBase[] = null;
    private _toClearRules: StreakTrackerRuleBase[] = [];

    /** @hidden */
    protected setContexts(): void
    {
        this._reelManagerRef.SetContext(ReelManager);
        this._playResultRef.SetContext(PlayResultComponent);
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.loadChildRules();
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._clearOnStates);

        this.configureDependencies();
    }

    /** @hidden */
    protected lateUpdate(): void
    {
        if (!this.executeContinuously || !this.isCurrentStreakState()) { return; }

        this.sendAllStreakEvents();
    }

    private sendAllStreakEvents(event: cc.Event.EventCustom = null): void
    {
        this._rules.forEach(rule => this.sendStreakEvents(rule, event));
    }

    /** Sends streak events to symbols based on rule results. */
    private sendStreakEvents = (rule: StreakTrackerRuleBase, event: cc.Event.EventCustom = null): void =>
    {
        if (event) { event.bubbles = true; }

        // Fetch symbol data from reel.
        this._reelManagerRef.WithValue(reelManager =>
            {
                let symbols: ISymbolData[] = reelManager.GetCurrentReelsData();
                if (!symbols || symbols.length < 1) { return; }

                let symbolsRemaining: number = reelManager.SymbolsRemaining;

                this.executeRules(rule, symbols, symbolsRemaining);
            });
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (this.executeOnState)
        {
            if (this._streakOnStates.some(x => x.Value === event.State))
            {
                this.sendAllStreakEvents();
                return;
            }
        }

        if (this._clearOnStates.some(x => x.Value === event.State))
        {
            this.clearStreakRules();
            return;
        }
    }

    /** @hidden */
    private configureDependencies(): void
    {
        if (!this._reelManagerRef.HasValue())
        {
            CLog.error("No reelManager configured. Disabled.", this, LogTypes.Initialization);
            this.enabled = false;
            return;
        }

        if (!this._playResultRef.HasValue())
        {
            CLog.error("No paylineManager configured. Disabled.", this, LogTypes.Initialization);
            this.enabled = false;
            return;
        }

        if (!this.executeOnState)
        {
            this._reelManagerRef.WithValue(reelManager =>
                {
                    this._rules.forEach(rule =>
                        {
                            reelManager.OnReelEvent(rule.StreakOnReelEvent, this.sendStreakEvents.bind(this, rule));

                            if (rule.ShouldClear)
                            {
                                reelManager.OnReelEvent(rule.ClearOnReelEvent, this.onClearHandler.bind(this, rule));
                            }
                        }, this);
                });
        }
    }

    /** Fetches child rules nested under parent. */
    private loadChildRules(): void
    {
        this._rules = this.getComponentsInChildren(StreakTrackerRuleBase).filter(x =>
            {
                x.Parent = this;
                return x.enabled;
            });
    }

    private isCurrentStreakState(): boolean { return this._streakOnStates.some(StateReference.IsCurrentFilter); }

    /**
     * Executes all rules belonging to this [[StreakTracker]].
     * @param symbols Symbols used to rule execution.
     * @param symbolsRemaining  Number of symbols not resolved.
     */
    private executeRules(rule: StreakTrackerRuleBase, symbols: ISymbolData[], symbolsRemaining: number): void
    {
        if (!this.isCurrentStreakState()) { return; }

        // Call rules against data list.
        const symbolCount: number = rule.ExecuteRule(symbols, symbolsRemaining).length;

        if (this.shouldClearRule(rule, symbolCount, symbolsRemaining))
        {
            this._toClearRules.push(rule);
        }
    }

    /**
     * Detertmins if rule is still capable of winning.
     * @returns True if rule is still viable.
     */
    private shouldClearRule(rule: StreakTrackerRuleBase, symbolCount: number, symbolsRemaing: number): boolean
    {
        if (rule.MaxSymbolCount && symbolCount >= rule.MaxSymbolCount) { return rule.HasStreaked; }
        if (rule.MinSymbolsToWin && symbolCount + symbolsRemaing < rule.MinSymbolsToWin) { return rule.HasStreaked; }
        return false;
    }

    /** Clears each rule in [[StreakTracker]]. */
    private clearStreakRules(): void
    {
        this._rules.forEach(rule => rule.ClearDispatched());
    }

    private onClearHandler(rule: StreakTrackerRuleBase): void
    {
        if (this._toClearRules.indexOf(rule) < 0) { return; }

        rule.ClearDispatched();

        const newToClear: StreakTrackerRuleBase[] = [];

        for (let toClearRule of this._toClearRules)
        {
            if (toClearRule === rule) { continue; }
            newToClear.push(toClearRule);
        }

        this._toClearRules = newToClear;
    }
}
